// The billing board's two kinds of card (owner, 2026-10-24).
//
// A printed bill frees its table for the next guests. The TABLE card shows only
// the table's current party — blue when running, plain when empty — and never the
// printed bill. Each printed bill gets its own card beside the table, drawn in the
// bill style and named like the table (no "(Bill)" suffix: the style says it).
//
// Bills are matched to tables by id. The table NUMBER is only a fallback for a bill
// that carries no table id: numbers repeat across floors.

export function pendingBillCards(table, pendingBills) {
  return (pendingBills || [])
    .filter(bill => (bill.table_id != null ? bill.table_id === table.id : bill.table_number == table.number))
    .map(bill => ({
      ...table,
      id: "pending-" + bill.id,
      isPendingBillCard: true,
      pendingBill: bill,
    }));
}

/** The printed bills the board shows, from the live active-orders feed. */
export function billedOrders(restOrders) {
  return (restOrders || []).filter(row => row.status === "billing");
}

/**
 * This device's mesh copy of the table's order, unless that order is now a printed
 * bill — then it is the bill's, not the table's current party.
 */
export function liveMeshOrder(meshOrder, restOrders) {
  if (!meshOrder || meshOrder.status === "billing") return null;
  const billed = billedOrders(restOrders).some(row => row.id === meshOrder.server_order_id);
  return billed ? null : meshOrder;
}

/**
 * Whether this device's copy may still paint a table the connected server has
 * nothing running on. Only for items punched here and not yet in the kitchen (a
 * draft on its way). A copy of an order the server knows (mapped), or one whose
 * every line the kitchen already has, is the server's to show: when the server
 * has no running order there, the party paid, left or moved. Owner 2026-10-24:
 * 6 and 7 Gardenn stayed blue on the billing PC after their bills printed.
 */
export function localDraftOnly(mesh) {
  if (!mesh || mesh.server_order_id) return false;
  return Object.values(mesh.items || {}).some(
    i => i.status !== "cancelled" && i.status !== "removed" && !i.kot_acked
  );
}

/**
 * The order the table card shows: this device's mesh copy merged with the
 * server's row, leaving out anything already printed as a bill.
 * `serverLive`: connected and the board read, so the server's answer stands.
 */
export function tableCardOrder({ meshOrder, restOrders, table, serverLive = false }) {
  const rows = restOrders || [];
  const restOrder = table
    ? rows.find(row => row.status !== "billing" && ((table.id != null && row.table_id === table.id) || row.table_number === table.number))
    : null;
  let mesh = liveMeshOrder(meshOrder, rows);
  if (serverLive && !restOrder && mesh && !localDraftOnly(mesh)) mesh = null;

  if (mesh && restOrder) {
    return {
      ...mesh,
      ...restOrder,
      table_id: mesh.table_id || restOrder.table_id || table?.id,
      aggregate_id: mesh.aggregate_id,
      amount: restOrder.amount ?? restOrder.total ?? mesh.amount,
      elapsed_seconds: restOrder.elapsed_seconds ?? mesh.elapsed_seconds,
      guest_count: restOrder.guest_count ?? mesh.guest_count,
      status: restOrder.status || mesh.status,
    };
  }
  return mesh || restOrder || null;
}

/** A printed bill's card names its bill number beside the guests (owner, 2026-10-24). */
/** What a printed bill's card shows as its amount: the bill total, as on the
 * Running orders board (owner, 2026-10-30: "show amount on printed bill cards like
 * you show amount on running table card"). Null when the row carries neither. */
export const billCardAmount = bill => bill?.bill_total ?? bill?.amount ?? null;

export const billCardNumber = bill =>
  bill?.bill_number === null || bill?.bill_number === undefined ? null : `Bill ${bill.bill_number}`;

/**
 * "Not sent 5m" when a table holds items punched but not sent to the kitchen for
 * at least the owner's unsent_items_warn_minutes (2026-10-24: items sat unsent on
 * a pad for 50 minutes and nobody knew). Null otherwise.
 */
export const unsentWarning = (order, warnMinutes) => {
  if (!order || !order.unsent_count || order.unsent_seconds == null) return null;
  const minutes = Number(warnMinutes);
  const after = Number.isFinite(minutes) && minutes >= 0 ? minutes : 3;
  if (order.unsent_seconds < after * 60) return null;
  return `Not sent ${Math.floor(order.unsent_seconds / 60)}m`;
};
